import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./customizeSpotTheme.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";

const PLACEHOLDER_SVG = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="600" height="400"><rect fill="#e8e8ec" width="100%" height="100%"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#999" font-family="system-ui,sans-serif" font-size="16">No image</text></svg>'
)}`;

const VALID_EXT = ["jpg", "jpeg", "png"];
const MAX_MB = 5;
const SPOT_DIM = { w: 600, h: 400 };

function leftKey(i) {
  return i === 1 ? "spotLeftImage" : `spotLeftImage${i}`;
}
function rightKey(i) {
  return i === 1 ? "spotRightImage" : `spotRightImage${i}`;
}
function svgMappingKey(i) {
  return i === 1 ? "spotSvgMapping" : `spotSvgMapping${i}`;
}
function gameTitleKey(i) {
  return i === 1 ? "game_title" : `game_title${i}`;
}

const getImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

/** RTK unwrap() rejects with a string from rejectWithValue — not an Error. */
function errMessage(e) {
  if (e == null) return "Unknown error";
  if (typeof e === "string") return e;
  return e.message || String(e);
}

const CustomizeSpotTheme = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data, status } = useSelector((s) => s.theme);

  const [numLayers, setNumLayers] = useState(1);
  const [titles, setTitles] = useState(Array(8).fill(""));
  const [doubleImage, setDoubleImage] = useState(true);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    const n = Number(data.number_of_layers);
    const layers =
      Number.isFinite(n) && n >= 1 && n <= 8
        ? n
        : data.multiimage_show === "true"
          ? 3
          : 1;
    setNumLayers(layers);
    setDoubleImage(data.uploadoption === "true" || data.uploadoption === true);
    setTitles(
      Array.from({ length: 8 }, (_, i) => {
        const k = gameTitleKey(i + 1);
        const v = data[k];
        return typeof v === "string" ? v : "";
      })
    );
  }, [data]);

  const imageUrl = useCallback(
    (filename) => {
      if (!filename || typeof filename !== "string") return PLACEHOLDER_SVG;
      const base = process.env.REACT_APP_S3_PATH || "";
      return `${base}${filename}`;
    },
    []
  );

  const handleSaveTitles = async () => {
    const n = numLayers;
    for (let i = 1; i <= n; i++) {
      const t = (titles[i - 1] || "").trim();
      if (!t) {
        Swal.fire(
          "Validation",
          `Game title ${i > 1 ? i : ""} is required for active layers.`,
          "warning"
        );
        return;
      }
      if (t.length > 200) {
        Swal.fire("Validation", `Game title ${i} must be at most 200 characters.`, "warning");
        return;
      }
    }

    const payloadData = { number_of_layers: String(n) };
    for (let i = 1; i <= 8; i++) {
      payloadData[gameTitleKey(i)] = (titles[i - 1] || "").trim();
    }

    const themeId = currentTheme || data?.themename;
    if (!themeId) {
      Swal.fire("No theme", "Select a theme from Admin home first.", "error");
      return;
    }

    setSaving(true);
    try {
      await updateThemeData({
        payload: { data: payloadData, currentTheme: themeId },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId })).unwrap();
      Swal.fire("Success", "Game settings saved.", "success");
    } catch (e) {
      Swal.fire("Error", errMessage(e) || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleDouble = async (next) => {
    const themeId = currentTheme || data?.themename;
    if (!themeId) {
      Swal.fire("No theme", "Select a theme from Admin home first.", "error");
      return;
    }
    setDoubleImage(next);
    try {
      await updateThemeData({
        payload: { data: { uploadoption: next ? "true" : "false" }, currentTheme: themeId },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId })).unwrap();
    } catch (e) {
      setDoubleImage(!next);
      Swal.fire("Error", errMessage(e) || "Could not update option", "error");
    }
  };

  const openUpload = (target) => {
    setUploadTarget(target);
    setUploadFile(null);
  };

  const closeUpload = () => {
    setUploadTarget(null);
    setUploadFile(null);
  };

  const submitUpload = async () => {
    if (!uploadTarget || !uploadFile) {
      Swal.fire("Select a file", "Choose an image to upload.", "info");
      return;
    }
    const themeName = currentTheme || data?.themename;
    if (!themeName || themeName === "undefined") {
      Swal.fire(
        "No theme",
        "No theme is selected. Go to Admin home, pick a theme, then try again.",
        "error"
      );
      return;
    }
    if (!adminToken) {
      Swal.fire("Not signed in", "Log in again and retry the upload.", "error");
      return;
    }
    const ext = uploadFile.name.split(".").pop().toLowerCase();
    if (!VALID_EXT.includes(ext)) {
      Swal.fire("Invalid format", "Only JPG, JPEG, PNG allowed.", "error");
      return;
    }
    if (uploadFile.size > MAX_MB * 1024 * 1024) {
      Swal.fire("Too large", `Max file size is ${MAX_MB} MB.`, "error");
      return;
    }
    try {
      const { width, height } = await getImageDimensions(uploadFile);
      if (width !== SPOT_DIM.w || height !== SPOT_DIM.h) {
        Swal.fire(
          "Invalid dimensions",
          `Image must be exactly ${SPOT_DIM.w}×${SPOT_DIM.h} pixels.`,
          "error"
        );
        return;
      }
    } catch {
      Swal.fire("Error", "Could not read image dimensions.", "error");
      return;
    }

    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("target", uploadTarget);
    fd.append("themeName", String(themeName));

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/uploadSpotImage`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${adminToken}` },
          body: fd,
        }
      );
      const text = await res.text();
      let json = {};
      try {
        json = text ? JSON.parse(text) : {};
      } catch {
        json = {};
      }
      if (!res.ok) {
        const detail =
          json.error ||
          json.message ||
          json.details ||
          (text && text.length < 400 ? text : null);
        throw new Error(
          detail || `Upload failed (HTTP ${res.status}). Is the API running on your backend?`
        );
      }

      try {
        await dispatch(fetchThemeData({ themeId: themeName })).unwrap();
      } catch (refreshErr) {
        Swal.fire({
          icon: "warning",
          title: "Image uploaded",
          text: `Saved to the server, but the page could not reload theme data: ${errMessage(
            refreshErr
          )}. Refresh the browser to see the new image.`,
        });
        closeUpload();
        return;
      }

      Swal.fire("Success", "Image updated.", "success");
      closeUpload();
    } catch (e) {
      Swal.fire("Error", errMessage(e) || "Upload failed", "error");
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="customize-spot-page">
        <p>Loading theme…</p>
      </div>
    );
  }

  return (
    <>
      <div className="back-button-holder">
        <button
          type="button"
          className="back-button"
          onClick={() => navigate("/admin/")}
        >
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="customize-spot-page">
        <div className="customize-spot-card">
          <h2>Game settings</h2>
          <div className="customize-spot-row">
            <label htmlFor="num-layers">Number of layers (1–8):</label>
            <select
              id="num-layers"
              value={numLayers}
              onChange={(e) => setNumLayers(parseInt(e.target.value, 10))}
            >
              {Array.from({ length: 8 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
          </div>

          <div id="game-titles-container">
            {Array.from({ length: 8 }, (_, idx) => {
              const i = idx + 1;
              if (i > numLayers) return null;
              return (
                <div key={i} className="layer-title-block" data-layer={i}>
                  <h4>Game Title{i > 1 ? ` ${i}` : ""}:</h4>
                  <input
                    type="text"
                    maxLength={200}
                    value={titles[idx]}
                    onChange={(e) => {
                      const next = [...titles];
                      next[idx] = e.target.value;
                      setTitles(next);
                    }}
                    placeholder="Enter title"
                  />
                  <div className="code-muted">* Maximum 200 characters.</div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            className="customize-spot-save"
            onClick={handleSaveTitles}
            disabled={saving}
          >
            Save
          </button>
        </div>

        <div className="customize-spot-card">
          <div className="customize-spot-toggle">
            <input
              type="checkbox"
              id="double-upload"
              checked={doubleImage}
              onChange={(e) => handleToggleDouble(e.target.checked)}
            />
            <label htmlFor="double-upload">Double image upload</label>
          </div>
          <div className="code-muted">
            When enabled, show “Normal image” and “Image with change” side by side.
          </div>
        </div>

        {Array.from({ length: 8 }, (_, idx) => {
          const i = idx + 1;
          if (i > numLayers) return null;
          const lk = leftKey(i);
          const rk = rightKey(i);
          const sk = svgMappingKey(i);
          const leftSrc = imageUrl(data?.[lk]);
          const rightSrc = imageUrl(data?.[rk]);

          return (
            <div key={i} className="layer-image-block" data-layer={i}>
              <div className="spot-image-row">
                {doubleImage && (
                  <div className="spot-image-col">
                    <div className="title-text">Normal Image</div>
                    <div className="map-image-container">
                      <img
                        src={leftSrc}
                        alt=""
                        className="spot-image"
                        onError={(e) => {
                          e.target.src = PLACEHOLDER_SVG;
                        }}
                      />
                      <button
                        type="button"
                        className="change-image"
                        onClick={() => openUpload(lk)}
                      >
                        Change Image
                      </button>
                    </div>
                  </div>
                )}
                <div
                  className={`spot-image-col ${doubleImage ? "" : "centered"}`}
                >
                  <div className="title-text">Image with change</div>
                  <div className="map-image-container">
                    <img
                      src={rightSrc}
                      alt=""
                      className="spot-image"
                      onError={(e) => {
                        e.target.src = PLACEHOLDER_SVG;
                      }}
                    />
                    <button
                      type="button"
                      className="change-image"
                      onClick={() => openUpload(rk)}
                    >
                      Change Image
                    </button>
                    <Link
                      className="map-object"
                      to={`/admin/spot-map?svgKey=${encodeURIComponent(
                        sk
                      )}&imageKey=${encodeURIComponent(rk)}${
                        doubleImage
                          ? `&leftImageKey=${encodeURIComponent(
                              lk
                            )}&doubleImage=1`
                          : ""
                      }`}
                    >
                      Map Object
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {uploadTarget && (
        <>
          <div
            className="customize-spot-modal-backdrop"
            onClick={closeUpload}
            aria-hidden
          />
          <div className="customize-spot-modal" role="dialog">
            <h3>Upload image</h3>
            <p className="hint">Target field: {uploadTarget}</p>
            <input
              type="file"
              accept="image/jpeg,image/png,image/jpg"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
            />
            <div className="hint">
              Allowed formats: JPG, JPEG, PNG. Dimensions: {SPOT_DIM.w} ×{" "}
              {SPOT_DIM.h} px. Max {MAX_MB} MB.
            </div>
            <div className="customize-spot-modal-actions">
              <button type="button" onClick={closeUpload}>
                Cancel
              </button>
              <button
                type="button"
                className="primary"
                onClick={submitUpload}
              >
                Upload
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CustomizeSpotTheme;
